import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, Text, ScrollView, Platform, StyleSheet, Dimensions, Modal, TouchableWithoutFeedback, TouchableOpacity } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector, useDispatch } from 'react-redux';
import SiteEvalActionPlanForm from './SiteEvalActionPlanForm';
import LhButton from '../../../components/LhButton';
import { useToast } from '../../../components/LhToastContainer';
import { AppDispatch, RootState } from '../../../store';
import { createApiClient, apiClient, getTimezoneOffsetString } from '../../../api/apiClient';
import { getSourceApiValue } from '../../ActionPlan/utils/formOptions';
import { selectSiteEvalActionPlans, selectActionPlanSessionId, addSiteEvalActionPlan, updateSiteEvalActionPlan } from '../../../store/siteEvalRedirectSlice';
import { useTheme } from '../../../theme';

interface SiteEvalActionPlanOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  title: string;
  questionId: string;
  questionTitle?: string; // Actual question title from Section_Value
  initialActionPlan?: string;
  initialSupervisor?: string;
  onSave: (actionPlan: string, supervisor: string) => void;
  isViewOnlyMode?: boolean;
}

/**
 * Custom Action Plan Overlay for Site Evaluation
 * Renders CreateActionPlanForm with Site Evaluation specific behavior
 */
const SiteEvalActionPlanOverlay: React.FC<SiteEvalActionPlanOverlayProps> = ({
  visible,
  onDismiss,
  title,
  questionId,
  questionTitle,
  initialActionPlan = '',
  initialSupervisor = '',
  onSave,
  isViewOnlyMode = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  
  // Helper to detect iOS mobile (not iPad)
  const isIOSMobile = () => {
    return Platform.OS === 'ios' && Dimensions.get('window').width < 768;
  };
  
  // Form submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitFn, setFormSubmitFn] = useState<(() => Promise<any>) | null>(null);
  const [formSetErrorsFn, setFormSetErrorsFn] = useState<((errors: { validationErrors?: Record<string, string>; submissionError?: any }) => void) | null>(null);

  // Dirty / unsaved-changes guard
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  // Ref for scrolling to top when warning banner appears on iOS/iPad
  const scrollViewRef = useRef<ScrollView>(null);

  // Scroll to top when the unsaved-changes warning is shown (all platforms)
  useEffect(() => {
    if (showUnsavedWarning) {
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    }
  }, [showUnsavedWarning]);

  // Get current user info from Redux for form initialization
  const authUserName = useSelector((state: RootState) => state.auth?.user?.name ?? state.auth?.user?.claims?.name ?? state.auth?.user?.username ?? '');
  const authUserId = useSelector((state: RootState) => state.auth?.user?.gpid ?? state.auth?.user?.id ?? null);
  const userInfoGPID = useSelector((state: RootState) => state.appData?.userInfo?.GPID ?? null);
  const userEmail = useSelector((state: RootState) => state.auth?.user?.email || state.auth?.user?.username || '');
  const userRole = useSelector((state: RootState) => state.appData?.userInfo?.roleName || 'Site Supervisor');
  const userRoleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId || 5);
  const selectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);
  const FieldUsers = useSelector((state: any) => state.appData.FieldUsers);
  const COEUsers = useSelector((state: any) => state.appData.COEUsers);
  const periodWeek = useSelector((state: RootState) => state.appData.periodWeek.data);
  
  // Get action plans from Redux
  const actionPlans = useSelector(selectSiteEvalActionPlans);
  const actionPlanSessionId = useSelector(selectActionPlanSessionId);

  // State to hold the current stored plan - refreshed when overlay opens
  const [storedPlan, setStoredPlan] = React.useState<any>(null);

  // Fetch stored action plan from Redux when overlay becomes visible
  React.useEffect(() => {
    if (visible && questionId) {
      const plan = actionPlans.find((plan: any) => plan.itemId === questionId || plan.relatedId === questionId) || null;
      console.log('[SiteEvalActionPlanOverlay] Retrieved stored plan from Redux for questionId:', questionId, plan);
      setStoredPlan(plan);
    } else if (!visible) {
      // Clear stored plan when overlay closes
      setStoredPlan(null);
    }
  }, [visible, questionId, actionPlans]);

  const isEditMode = Boolean(storedPlan && (initialActionPlan || storedPlan.actionPlan));

  // Format user name helper - defined before formData
  const formatUserNameHelper = React.useCallback((email: string) => {
    if (!email) return '';
    const allUsers = [...(FieldUsers || []), ...(COEUsers || [])];
    const user = allUsers.find((u: any) => u.UserEmail === email);
    return user ? user.UserEmail : email;
  }, [FieldUsers, COEUsers]);

  // Format employee name helper - defined before formData
  const formatEmployeeNameHelper = React.useCallback((emailOrId: string) => {
    if (!emailOrId) return '';
    const allUsers = [...(FieldUsers || []), ...(COEUsers || [])];
    const user = allUsers.find((u: any) => u.UserEmail === emailOrId || u.id === emailOrId);
    if (user) {
      const emailPart = user.UserEmail?.split('@')[0] || '';
      const nameParts = emailPart.split(/[._-]/);
      return nameParts.map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ') || user.UserEmail;
    }
    const emailPart = emailOrId.split('@')[0] || emailOrId;
    const nameParts = emailPart.split(/[._-]/);
    return nameParts.map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ') || emailOrId;
  }, [FieldUsers, COEUsers]);

  // Initialize form data with Site Evaluation specific values
  // This object is recreated when storedPlan changes (which happens when overlay opens)
  const formData = useMemo(() => {
    console.log('[SiteEvalActionPlanOverlay] Building formData with storedPlan:', storedPlan);
    
    // Get values from storedPlan.rawFormData first (contains complete form objects)
    // Fall back to flat storedPlan fields, then to initial props
    const raw = storedPlan?.rawFormData || {};
    
    // Helper to safely convert date strings/objects to Date objects
    const toDate = (value: any) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      try {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
      } catch {
        return null;
      }
    };
    
    // Helper to ensure we have a proper dropdown option object
    const toDropdownOption = (value: any, fallbackId?: string, fallbackDisplayText?: string) => {
      if (!value) return null;
      // If it's already an object with id and displayText, use it
      if (typeof value === 'object' && value.id && value.displayText) {
        return value;
      }
      // If it's a string, create an object
      if (typeof value === 'string') {
        const lowerId = value.toLowerCase();
        return { id: lowerId, displayText: value };
      }
      // Fallback
      if (fallbackId && fallbackDisplayText) {
        return { id: fallbackId, displayText: fallbackDisplayText };
      }
      return null;
    };
    
    return {
      title: raw.title || storedPlan?.title || '',
      source: { id: 'site-evaluation', displayText: 'Site Evaluation' },
      type: raw.selectedType || raw.type || toDropdownOption(storedPlan?.type),
      priority: raw.selectedPriority || raw.priority || toDropdownOption(storedPlan?.priority),
      owner: { id: authUserId ?? 'owner', displayText: authUserName || '--' },
      assignee: raw.selectedAssignee || raw.assignee || (storedPlan?.assignee ? { id: storedPlan.assignee, displayText: formatUserNameHelper(storedPlan.assignee) } : (initialSupervisor ? { id: initialSupervisor, displayText: formatUserNameHelper(initialSupervisor) } : null)),
      startDate: toDate(raw.startDate) || toDate(storedPlan?.startDate),
      targetDate: toDate(raw.targetDate) || toDate(storedPlan?.targetDate),
      actionPlan: raw.actionPlan || storedPlan?.actionPlan || initialActionPlan || '',
      notes: raw.notes || storedPlan?.notes || '',
      frontlineInvolvement: raw.frontlineInvolvement ?? storedPlan?.frontlineInvolvement ?? false,
      employee: raw.selectedEmployee || raw.employee || (storedPlan?.employee ? { id: storedPlan.employee, displayText: formatEmployeeNameHelper(storedPlan.employee) } : null),
      _siteEvaluationMode: true,
      questionId: questionId,
      _isEditMode: isEditMode,
    };
  }, [storedPlan, authUserId, authUserName, initialSupervisor, initialActionPlan, questionId, isEditMode, formatUserNameHelper, formatEmployeeNameHelper]);

  // Handle form submission with custom save logic for site evaluation
  const handleFormSubmit = useCallback(async (formResult: any) => {
    try {
      setIsSubmitting(true);

      // Extract action plan and assignee from form data
      const actionPlan = formResult.actionPlan || formResult.ACTION_PLAN_DESCRIPTION || '';
      const assigneeEmail = formResult.assignee?.email || formResult.assignee?.id || '';
      const assigneeGpid = formResult.assignee?.gpid || formResult.assignee?.id || formResult.assignee_gpid || authUserId;
      
      const ownerGpid = authUserId || userInfoGPID || '';
      const ownerEmail = userEmail || '';
      const ownerRole = userRole ;
      const ownerRoleId = userRoleId ;
      
      // Get action_plan_session_id from Redux
      console.log('[SiteEvalActionPlanOverlay] Using action_plan_session_id from Redux:', actionPlanSessionId);
      
      // Determine frontline involvement and employee values
      const frontlineInvolvement = !!(formResult.frontlineInvolvement || formResult.frontline_employee_involvement);
      const employeeGpidValue = formResult.employee?.id || formResult.selectedEmployee?.id || formResult.employee?.gpid || '';
      const employeeNameValue = formResult.employee?.displayText || formResult.selectedEmployee?.displayText || '';

      // Build the POST API payload according to the spec
      const apiPayload = {
        title: formResult.title || '',
        source: 'Site_Evaluation', // Use underscore format for database constraint
        type: formResult.type?.displayText || formResult.type?.id || formResult.selectedType?.displayText || formResult.selectedType?.id ,
        priority: formResult.priority?.displayText || formResult.priority?.id || formResult.selectedPriority?.displayText || formResult.selectedPriority?.id ,
        period: periodWeek?.period || '',
        week: periodWeek?.week || '',
        location: selectedLocation?.displayText || '',
        assignee_gpid: assigneeGpid,
        assignee_email: assigneeEmail,
        assignee_role: formResult.assignee?.role || formResult.assignee?.role_name,
        assignee_role_id: formResult.assignee?.role_id || formResult.assignee?.roleId,
        // Action plan owner fields (current user creating the action plan)
        action_plan_owner_gpid: ownerGpid,
        action_plan_owner_email: ownerEmail,
        action_plan_owner_role: ownerRole,
        action_plan_owner_role_id: ownerRoleId,
        // Created by fields (same as owner for new action plans)
        created_by: ownerGpid,
        created_by_email: ownerEmail,
        created_by_role_id: ownerRoleId,
        // Location, period, and week fields
        start_date: formResult.startDate ? formResult.startDate: null,
        target_date: formResult.targetDate ? formResult.targetDate: null,
        action_plan_description: actionPlan,
        notes: formResult.notes || formResult.NOTES || '',
        status: 'Completed', 
        frontline_employee_involvement: frontlineInvolvement,
        ...(frontlineInvolvement ? {
          involved_employee_gpids: employeeGpidValue,
          involved_employee_names: employeeNameValue,
        } : {}),
        capabilities: [
          {
            is_parent: false,
            question_id: parseInt(questionId.includes('_') ? questionId.split('_')[questionId.split('_').length - 1] : questionId) || 0,
            question_title: questionTitle || title || 'Site Evaluation Action Plan'
          }
        ],
        session_id: actionPlanSessionId,
        clientTimestamp: new Date().toISOString(),
        is_standup: false,
        is_source: true,
        top_opportunity: false
      };

      console.log('[SiteEvalActionPlanOverlay] Making POST API call with payload:', apiPayload);

      // Make the POST API call (or PUT when editing an existing site-eval action plan)
      const api = createApiClient();
      let response: any;

      // If overlay was opened for edit and we have an existing server-side action plan id, call PUT
      if (isEditMode && storedPlan && (storedPlan.actionPlanId || storedPlan.id)) {
        const updateId = storedPlan.actionPlanId || storedPlan.id;
        const putPayload: any = {
          title: formResult.title || '',
          type: formResult.type?.displayText || formResult.type?.id || null,
          priority: formResult.priority?.displayText || formResult.priority?.id || null,
          start_date: formResult.startDate ? new Date(formResult.startDate).toISOString().split('T')[0] : undefined,
          target_date: formResult.targetDate ? new Date(formResult.targetDate).toISOString().split('T')[0] : undefined,
          action_plan_description: actionPlan,
          session_id: actionPlanSessionId,
          is_temp:storedPlan?.is_temp? storedPlan.is_temp: false,
          is_siteeval:true,
          clientTimestamp: new Date().toISOString(),
          assignee_email: assigneeEmail,
          assignee_gpid: assigneeGpid,
          notes: formResult.notes || '',
          updated_by: ownerGpid,
          updated_by_email: ownerEmail,
          updated_by_role_id: ownerRoleId,
          // Send frontline flag so backend knows checkbox state on edits
          frontline_employee_involvement: frontlineInvolvement,
          // Only send employee fields when frontline involvement is true
          ...(frontlineInvolvement ? {
            employee_gpid: employeeGpidValue,
            employee_name: employeeNameValue,
          } : {}),
        };

        console.log('[SiteEvalActionPlanOverlay] Making PUT API call with payload:', putPayload);
        const clientTimezone = getTimezoneOffsetString();
        response = await apiClient(api, `/action-plans/${updateId}?clientTimezone=${encodeURIComponent(clientTimezone)}`, 'PUT', putPayload);
        console.log('[SiteEvalActionPlanOverlay] PUT API response:', response);
      } else {
        const clientTimezone = getTimezoneOffsetString();
        response = await apiClient(api, `/action-plans/source?clientTimezone=${encodeURIComponent(clientTimezone)}`, 'POST', apiPayload);

        console.log('[SiteEvalActionPlanOverlay] POST API response:', response);
      }

      // Store comprehensive action plan data in Redux
      const actionPlanData = {
        id: response?.data?.id || response?.id || (storedPlan?.id || Date.now().toString()),
        localId: storedPlan?.localId || Date.now(),
        itemId: questionId,
        relatedId: questionId,
        title: formResult.title || '',
        actionPlan,
        supervisor: assigneeEmail,
        assignee: assigneeEmail,
        source: 'Site_Evaluation',
        type: formResult.type?.displayText || formResult.type?.id || formResult.selectedType?.displayText || formResult.selectedType?.id || null,
        priority: formResult.priority?.displayText || formResult.priority?.id || formResult.selectedPriority?.displayText || formResult.selectedPriority?.id || null,
        startDate: formResult.startDate ? formResult.startDate : null,
        targetDate: formResult.targetDate ? formResult.targetDate : null,
        notes: formResult.notes || '',
        frontlineInvolvement: formResult.frontlineInvolvement || false,
        employee: formResult.employee?.id || formResult.selectedEmployee?.id || null,
        employeeName: formResult.employee?.displayText || formResult.selectedEmployee?.displayText || null,
        // Store API response data
        actionPlanId: response?.data?.actionPlanId || response?.data?.id || response?.id || (storedPlan?.actionPlanId || null),
        is_temp: response?.data?.is_temp ?? storedPlan?.is_temp ?? false,
        apiResponse: response,
        // Store session_id for site-evaluation-enhanced API call
        session_id: actionPlanSessionId || '',
        // Store raw form data for debugging and complete restoration
        rawFormData: formResult,
        createdAt: storedPlan?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isReference: false,
        status: 'Active'
      };
      
      // Dispatch to Redux - update if exists, add if new
      if (storedPlan) {
        dispatch(updateSiteEvalActionPlan(actionPlanData));
        console.log('Action plan updated successfully in Redux:', actionPlanData);
      } else {
        dispatch(addSiteEvalActionPlan(actionPlanData));
        console.log('Action plan created successfully in Redux:', actionPlanData);
      }

      // Call the original save handler with the required data
      onSave(actionPlan, assigneeEmail);

      // Show success notification
      toast.showToast({
        header: '',
        subtext: isEditMode ? 'Action plan has been updated' : 'Action plan has been created',
        variant: 'success',
        position: 'top',
        duration: 4000,
      });

      // Close the overlay
      onDismiss();
    } catch (error: any) {
      console.error('Error saving site evaluation action plan:', error);
      
      // Extract error details from API response
      let errorMessage = error?.response?.data?.message || error?.message || (isEditMode ? 'Failed to update action plan' : 'Failed to save action plan');
      let errorTitle = 'Error';
      let errorDetails = null;
      const validationErrors: Record<string, string> = {};

      // Handle validation errors from backend
      if (error?.response?.data?.details?.missingFields) {
        const missingFields = error.response.data.details.missingFields;
        
        // Map backend field names to friendly labels
        const fieldLabelMap: Record<string, string> = {
          title: 'Title',
          source: 'Source',
          type: 'Type',
          priority: 'Priority',
          assignee: 'Assignee',
          assignee_gpid: 'Assignee',
          assignee_email: 'Assignee',
          assignee_role: 'Assignee',
          assignee_role_id: 'Assignee',
          start_date: 'Start Date',
          target_date: 'Target Date',
          action_plan: 'Action Plan',
          action_plan_description: 'Action Plan',
          employee: 'Employee Name',
          employee_gpid: 'Employee Name',
        };

        // Build field-level validation errors
        missingFields.forEach((field: string) => {
          const label = fieldLabelMap[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          validationErrors[field] = `${label} is required`;
        });

        // Aggregate assignee-related errors into a single 'assignee' key
        const assigneeRelatedKeys = Object.keys(validationErrors).filter(k => k.startsWith('assignee'));
        if (assigneeRelatedKeys.length > 0) {
          const combined = assigneeRelatedKeys
            .map(k => validationErrors[k])
            .filter(Boolean)
            .join('; ');
          validationErrors['assignee'] = combined || 'Assignee is required';
          assigneeRelatedKeys.forEach(k => delete validationErrors[k]);
        }

        // Aggregate employee-related backend keys into single 'employee' key
        const employeeRelatedKeys = Object.keys(validationErrors).filter(k => k.startsWith('employee'));
        if (employeeRelatedKeys.length > 0) {
          const combinedEmp = employeeRelatedKeys.map(k => validationErrors[k]).filter(Boolean).join('; ');
          validationErrors['employee'] = combinedEmp || 'Employee Name is required';
          employeeRelatedKeys.forEach(k => delete validationErrors[k]);
        }

        // Get unique friendly names for missing fields
        const uniqueLabels = new Set<string>();
        missingFields.forEach((field: string) => {
          const label = fieldLabelMap[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          uniqueLabels.add(label);
        });

        const friendlyNames = Array.from(uniqueLabels);
        errorMessage = `Required fields are missing: ${friendlyNames.join(', ')}`;
        errorTitle = 'Validation Error';
        errorDetails = { missingFields };
      } else if (error?.response?.data?.errors) {
        // Handle other validation errors
        const errors = error.response.data.errors;
        Object.entries(errors).forEach(([key, value]) => {
          if (value) {
            validationErrors[key] = String(value);
          }
        });
        const errorMessages = Object.values(errors).filter(Boolean).join('; ');
        if (errorMessages) {
          errorMessage = errorMessages;
          errorTitle = 'Validation Error';
        }
      }

      // Pass validation errors back to the form for inline display
      if (formSetErrorsFn && Object.keys(validationErrors).length > 0) {
        formSetErrorsFn({
          validationErrors,
          submissionError: {
            title: errorTitle,
            message: errorMessage,
          }
        });
      }
      
      // Show error toast
      toast.showToast({
        header: errorTitle,
        subtext: errorMessage,
        variant: 'error',
        position: 'top',
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    authUserId,
    userInfoGPID,
    userEmail,
    userRole,
    userRoleId,
    periodWeek,
    selectedLocation,
    questionId,
    title,
    isEditMode,
    storedPlan,
    onSave,
    onDismiss,
    dispatch,
    formSetErrorsFn,
    actionPlanSessionId,
  ]);

  // Handle form ready callback to get submit function and error-setting function
  const handleFormSubmitReady = useCallback((submitFn: () => Promise<any>, setErrorsFn?: (errors: { validationErrors?: Record<string, string>; submissionError?: any }) => void) => {
    setFormSubmitFn(() => submitFn);
    if (setErrorsFn) {
      setFormSetErrorsFn(() => setErrorsFn);
    }
  }, []);

  // Handle save button press
  const handleSavePress = useCallback(async () => {
    if (formSubmitFn && !isSubmitting) {
      try {
        const result = await formSubmitFn();
        if (result) {
          // Form validation passed, proceed with API call
          await handleFormSubmit(result);
        } else {
          // Form validation failed, show toast (error banner already shown in form)
          toast.showToast({
            header: 'Validation Error',
            subtext: 'Please fill in all required fields',
            variant: 'error',
            position: 'top',
            duration: 4000,
          });
        }
      } catch (error) {
        console.error('Form submission failed:', error);
      }
    }
  }, [formSubmitFn, isSubmitting, handleFormSubmit, toast]);

  // Reset form state when overlay closes OR resets when it opens (guards against batched state updates)
  useEffect(() => {
    if (!visible) {
      setFormSubmitFn(null);
      setFormSetErrorsFn(null);
      setIsSubmitting(false);
      setIsFormDirty(false);
      setShowUnsavedWarning(false);
    } else {
      // Reset dirty state every time the overlay becomes visible so a
      // previously-dirty session never leaks into a fresh open
      setIsFormDirty(false);
      setShowUnsavedWarning(false);
    }
  }, [visible]);

  // Guarded backdrop dismiss – only close when there are no unsaved changes
  const handleBackdropPress = useCallback(() => {
    if (isFormDirty) {
      setShowUnsavedWarning(true);
    } else {
      onDismiss();
    }
  }, [isFormDirty, onDismiss]);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onDismiss}
    >
      {/* modalBackdrop is a plain flex container that centres the modal box.
          The backdrop press target is a separate absolute-fill TouchableWithoutFeedback
          that sits BEHIND the modal box — this prevents it from intercepting touches
          inside the modal content on iOS/iPad. */}
      <View style={overlayStyles.modalBackdrop}>
        <TouchableWithoutFeedback onPress={handleBackdropPress}>
          <View style={StyleSheet.absoluteFillObject} />
        </TouchableWithoutFeedback>

        <View style={[overlayStyles.modalContainer, isDark && { backgroundColor: colors.background.elevated }]}>
              {/* Header */}
              <View style={[overlayStyles.header, isDark && { backgroundColor: colors.background.elevated }]}>
                <Text style={[overlayStyles.headerTitle, isDark && { color: colors.text.primary }]}>
                  {isEditMode ? 'Edit Action Plan' : 'Action Plan'}
                </Text>
              </View>
              
              {/* Scrollable Content including Form and Buttons */}
              <ScrollView
                ref={scrollViewRef}
                style={[overlayStyles.scrollView, isDark && { backgroundColor: colors.background.elevated }]}
                contentContainerStyle={overlayStyles.scrollViewContent}
                showsVerticalScrollIndicator={true}
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'none'}
                contentInsetAdjustmentBehavior={Platform.OS === 'ios' ? 'automatic' : undefined}
                scrollIndicatorInsets={Platform.OS === 'ios' ? { right: 1 } : undefined}
              >
                {/* Unsaved changes warning banner */}
                {showUnsavedWarning && (
                  <View
                    style={[
                      overlayStyles.warningBanner,
                      isDark && { backgroundColor: colors.statusBackground.warning, borderColor: colors.status.warning },
                    ]}
                  >
                    <View style={overlayStyles.warningBannerContent}>
                      <MaterialIcons name="warning" size={20} color={isDark ? colors.status.warning : "#E65100"} />
                      <View style={overlayStyles.warningTextContainer}>
                        <Text style={[overlayStyles.warningMessage, isDark && { color: colors.text.primary }]}>
                          You have unsaved changes. If you still want to close, please click the{' '}
                          <Text style={overlayStyles.warningMessageBold}>Cancel</Text> button.
                        </Text>
                      </View>
                    </View>
                    <TouchableOpacity
                      onPress={() => setShowUnsavedWarning(false)}
                      style={overlayStyles.warningCloseButton}
                    >
                      <MaterialIcons name="close" size={20} color={isDark ? colors.status.warning : "#E65100"} />
                    </TouchableOpacity>
                  </View>
                )}

                <SiteEvalActionPlanForm
                  data={formData}
                  onSubmit={handleFormSubmit}
                  onCancel={onDismiss}
                  onFormSubmitReady={handleFormSubmitReady}
                  onFormDirty={() => { setIsFormDirty(true); setShowUnsavedWarning(false); }}
                />

                {/* Buttons inside scrollable content */}
                <View style={[isIOSMobile() ? overlayStyles.buttonsContainerMobile : overlayStyles.buttonsContainer, isDark && { backgroundColor: colors.background.elevated }]}>
                  <View style={isIOSMobile() ? overlayStyles.buttonWrapperMobile : overlayStyles.buttonWrapper}>
                    <LhButton 
                      text="Cancel" 
                      variant="tertiary" 
                      size="medium" 
                      onPress={onDismiss} 
                      style={isIOSMobile() ? overlayStyles.cancelButtonMobile : overlayStyles.cancelButton}
                      textStyle={[overlayStyles.cancelButtonText, isDark && { color: colors.brand.primary }]}
                      disabled={isViewOnlyMode || isSubmitting} 
                    />
                  </View>
                  <View style={isIOSMobile() ? overlayStyles.buttonWrapperMobile : overlayStyles.buttonWrapper}>
                    <LhButton 
                      text={isEditMode ? "Save" : "Save"} 
                      variant="primary" 
                      size="medium" 
                      onPress={handleSavePress} 
                      style={isIOSMobile() ? overlayStyles.saveButtonMobile : overlayStyles.saveButton}
                      textStyle={overlayStyles.saveButtonText}
                      disabled={isViewOnlyMode || isSubmitting || !formSubmitFn} 
                    />
                  </View>
                </View>
              </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

// Styles specific to the overlay matching Figma design
const overlayStyles = StyleSheet.create({
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    width: Platform.OS === 'web' ? '90%' : '95%',
    maxWidth: 1050,
    maxHeight: Dimensions.get('window').height * 0.9,
    ...Platform.select({
      ios: {
        flex: 1,
        marginVertical: 50,
        height: undefined,
      },
      web: {
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 20,
        elevation: 10,
      },
    }),
  },
  header: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 0,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  headerTitle: {
    fontFamily: Platform.select({
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
      ios: 'Inter',
      android: 'Inter'
    }),
    fontWeight: '700',
    fontSize: 24,
    lineHeight: 32,
    color: '#2D2D2D',
    textAlign: 'left',
  },
  scrollView: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    ...Platform.select({
      ios: {
        minHeight: 200,
      },
    }),
  },
  scrollViewContent: {
    paddingBottom: Platform.OS === 'ios' ? 40 : 24,
    flexGrow: 1,
    ...Platform.select({
      ios: {
        minHeight: 400,
      },
    }),
  },
  buttonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
    paddingTop: 0,
    paddingBottom: 4,
    backgroundColor: '#FFFFFF',
  },
  buttonsContainerMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
    paddingHorizontal: 24,
    paddingTop: 0,
    paddingBottom: 4,
    backgroundColor: '#FFFFFF',
  },
  buttonWrapper: {
    width: 'auto',
    minWidth: 80,
    justifyContent: 'center',
  },
  buttonWrapperMobile: {
    width: '100%',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingVertical: 4,
    paddingHorizontal: 0,
    borderRadius: 24,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 80,
  },
  cancelButtonMobile: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingVertical: 12,
    paddingHorizontal: 0,
    borderRadius: 24,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  cancelButtonText: {
    color: '#034895',
    fontFamily: Platform.select({ 
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', 
      ios: 'Inter', 
      android: 'Inter' 
    }),
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 20,
  },
  saveButton: {
    backgroundColor: '#034895',
    borderWidth: 0,
    paddingVertical: 4,
    paddingHorizontal: 16,
    borderRadius: 24,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 80,
  },
  saveButtonMobile: {
    backgroundColor: '#034895',
    borderWidth: 0,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 24,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({ 
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', 
      ios: 'Inter', 
      android: 'Inter' 
    }),
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 20,
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: '#FFF3E0',
    borderWidth: 1,
    borderColor: '#FFB74D',
    borderRadius: 6,
    marginHorizontal: 24,
    marginBottom: 8,
    padding: 12,
  },
  warningBannerContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  warningTextContainer: {
    flex: 1,
  },
  warningMessage: {
    fontFamily: Platform.select({
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
      ios: 'Inter',
      android: 'Inter',
    }),
    fontSize: 13,
    color: '#E65100',
    lineHeight: 18,
  },
  warningMessageBold: {
    fontWeight: '700',
  },
  warningCloseButton: {
    padding: 2,
    marginLeft: 8,
  },
});

export default SiteEvalActionPlanOverlay;